import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, ArrowLeft, Store } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicCampaignPage() {
  const { slug, campaignSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/store/${slug}/campaign/${campaignSlug}`);
        if (!res.ok) throw new Error('Campaign not found');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, campaignSlug]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center text-center px-4">
      <div>
        <Gift className="w-16 h-16 text-gray-700 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Campaign Not Found</h2>
        <p className="text-gray-500 text-sm mb-4">This campaign doesn't exist or has ended.</p>
        {slug && (
          <Link to={`/store/${slug}`} className="text-sm text-blue-400 hover:text-blue-300">
            Back to store
          </Link>
        )}
      </div>
    </div>
  );

  const { campaign, restaurant } = data;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="public-campaign-page">
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to={`/store/${slug}`}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
            data-testid="back-to-store">
            <ArrowLeft className="w-4 h-4" />
            {restaurant.name}
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {campaign.banner_image_url && (
          <img src={campaign.banner_image_url} alt={campaign.name}
            className="w-full h-52 object-cover rounded-2xl mb-6" />
        )}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {restaurant.image_url ? (
                <img src={restaurant.image_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
              ) : (
                <Store className="w-8 h-8 text-gray-500" />
              )}
              <span className="text-sm text-gray-400">{restaurant.name}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="campaign-title">
              {campaign.name}
            </h1>
            {campaign.description && (
              <p className="text-gray-400 mt-2 text-sm leading-relaxed">{campaign.description}</p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-bold text-white">${Number(campaign.gift_card_amount || 0).toFixed(2)}</div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              campaign.campaign_type === 'free'
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-blue-500/15 text-blue-400'
            }`}>
              {campaign.campaign_type === 'free' ? 'FREE' : 'Purchase'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-8">
          <span>{campaign.remaining} of {campaign.quantity_limit} remaining</span>
          {campaign.end_date && (
            <span>Ends {new Date(campaign.end_date).toLocaleDateString()}</span>
          )}
        </div>

        {/* Gift cards are bought through the store's live gift card flow */}
        <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-8 text-center">
          <Gift className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <Link
            to={`/store/${slug}/gift-cards`}
            className="inline-block rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500"
            data-testid="campaign-buy-gift-card"
          >
            Get a gift card
          </Link>
        </div>
      </div>
    </div>
  );
}
